import React, { useState, useEffect } from 'react';

export default function ChallengePlatform({ challengeType, onClose, onSubmitFlag }) {
  // Types: 'fullstack' or 'frontend'
  const isFullStack = challengeType === 'fullstack';

  // Initial code states
  const initialFiles = isFullStack ? {
    'backend/auth.js': `const jwt = require('jsonwebtoken');
const JWT_SECRET = 'techcorp_super_secret_key_2026';

module.exports = function verifyToken(req, res, next) {
  const authHeader = req.headers['authorization'];
  
  // ❌ BUG: Below line is incorrect. It needs to extract the token correctly.
  // TODO: Fix JWT token parsing. Extract the token correctly from authHeader (format: "Bearer <token>").
  const token = authHeader; 
  
  if (!token) {
    return res.status(401).json({ error: 'Access denied. No token provided.' });
  }

  try {
    const decoded = jwt.verify(token, JWT_SECRET);
    req.user = decoded;
    next();
  } catch (ex) {
    res.status(400).json({ error: 'Invalid token.' });
  }
};`,
    'backend/server.js': `const express = require('express');
const http = require('http');
const socketIo = require('socket.io');

const app = express();
const server = http.createServer(app);
const io = socketIo(server);

app.use(express.json());

let tasks = [];

app.post('/api/tasks', require('./auth'), (req, res) => {
  const { title } = req.body;
  const task = { id: tasks.length + 1, title, completed: false };
  tasks.push(task);

  // ❌ BUG: WebSocket broadcast is commented out!
  // TODO: Uncomment the line below to broadcast task creation to all connected clients.
  // io.emit('task_created', task);

  res.status(201).json(task);
});`,
    'frontend/src/App.jsx': `import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';

export default function TaskApp() {
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const newSocket = io('http://localhost:5000');
    setSocket(newSocket);
    
    newSocket.on('task_created', (task) => {
      setTasks(prev => [...prev, task]);
    });

    return () => newSocket.close();
  }, []);

  return (
    <div className="task-container">
      <h2>TechCorp Live Tasks</h2>
    </div>
  );
}`
  } : {
    'src/hooks/useCachedFetch.js': `import { useState, useEffect } from 'react';

const cache = {};

export function useCachedFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url) return;

    // ❌ BUG: The hook makes fetch network requests on every render without checking cache.
    // TODO: Implement cache lookup (using the cache object) to return memoized data before fetching.
    const fetchFromNetwork = async () => {
      setLoading(true);
      const response = await fetch(url);
      const result = await response.json();
      cache[url] = result;
      setData(result);
      setLoading(false);
    };

    fetchFromNetwork();
  }, [url]);

  return { data, loading };
}`,
    'src/components/UserList.jsx': `import React, { useState } from 'react';
import { useCachedFetch } from '../hooks/useCachedFetch';

export default function UserList() {
  const [query, setQuery] = useState('');
  const { data: users, loading } = useCachedFetch(\`/api/users?q=\${query}\`);

  return (
    <div className="user-list">
      <input 
        value={query} 
        onChange={e => setQuery(e.target.value)} 
        placeholder="Filter users..." 
      />
    </div>
  );
}`,
    'src/App.jsx': `import React from 'react';
import UserList from './components/UserList';

export default function App() {
  return (
    <div className="app">
      <h1>Performance Sandbox</h1>
      <UserList />
    </div>
  );
}`
  };

  const [files, setFiles] = useState(initialFiles);
  const [activeFile, setActiveFile] = useState(isFullStack ? 'backend/auth.js' : 'src/hooks/useCachedFetch.js');
  const [editorContent, setEditorContent] = useState(initialFiles[activeFile]);
  const [logs, setLogs] = useState([
    `$ systemctl start challenge-sandbox`,
    `Initializing platform.io sandboxed runtime...`,
    `[INFO] Dev environment booted successfully.`,
    isFullStack 
      ? `[WARN] JWT validation returning 401: Token malformed.` 
      : `[WARN] Redundant network fetches detected on every state mutation.`
  ]);
  
  // Tab states
  const [activeRightTab, setActiveRightTab] = useState('preview'); // 'preview' | 'tests'
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [testsPassed, setTestsPassed] = useState(false);
  
  // Live simulation states (Mock database state inside frontend browser preview)
  const [mockTasks, setMockTasks] = useState([
    { id: 1, title: 'Implement WebSocket integration', completed: true },
    { id: 2, title: 'Secure API with custom JWT middleware', completed: false }
  ]);
  const [taskInput, setTaskInput] = useState('');
  const [notification, setNotification] = useState(null);

  // Cache hit tracker for Frontend Challenge
  const [searchQuery, setSearchQuery] = useState('');
  const [networkFetchCount, setNetworkFetchCount] = useState(2);
  const [cacheHitCount, setCacheHitCount] = useState(0);

  // Sync editor change with file dictionary
  const handleEditorChange = (e) => {
    const val = e.target.value;
    setEditorContent(val);
    setFiles(prev => ({
      ...prev,
      [activeFile]: val
    }));
  };

  // Sync active file content
  const selectFile = (fileName) => {
    setActiveFile(fileName);
    setEditorContent(files[fileName]);
  };

  // Check if bug is resolved
  const isAuthFixed = () => {
    const code = files['backend/auth.js'] || '';
    // Resolves if they split, slice, replace or check Bearer format correctly
    return code.includes('split') || code.includes('replace') || code.includes('slice') || code.includes('substring') || code.includes('Bearer');
  };

  const isWSFixed = () => {
    const code = files['backend/server.js'] || '';
    // Uncommenting the WebSocket broadcast line
    return code.includes("io.emit('task_created'") || code.includes('io.emit("task_created"');
  };

  const isCacheFixed = () => {
    const code = files['src/hooks/useCachedFetch.js'] || '';
    // Checks cache[url] before performing fetch
    return code.includes('cache[url]') && (code.includes('return') || code.includes('setData'));
  };

  // Automatically trigger dynamic UI updates based on live code states
  const authStatus = isFullStack ? isAuthFixed() : false;
  const wsStatus = isFullStack ? isWSFixed() : false;
  const cacheStatus = !isFullStack ? isCacheFixed() : false;

  // Run mock tests suite
  const runTestSuite = () => {
    setIsRunningTests(true);
    setActiveRightTab('tests');
    
    // Add logs
    setLogs(prev => [
      ...prev,
      `\n$ npm run test-suite`,
      `[JEST] Scanning test specs...`,
      `Found 2 test files.`
    ]);

    setTimeout(() => {
      if (isFullStack) {
        const authFixed = isAuthFixed();
        const wsFixed = isWSFixed();

        setLogs(prev => [
          ...prev,
          `\n● Running auth.test.js...`,
          authFixed ? `  ✓ auth.test.js - Bearer JWT Token successfully authenticated (32ms)` : `  ✕ auth.test.js - Access denied. Bearer prefix is not parsed (failed)`,
          `\n● Running websockets.test.js...`,
          wsFixed ? `  ✓ websockets.test.js - Live task creation broadcast working (18ms)` : `  ✕ websockets.test.js - Event 'task_created' not broadcasted (failed)`
        ]);

        setIsRunningTests(false);
        if (authFixed && wsFixed) {
          setTestsPassed(true);
          setLogs(prev => [
            ...prev,
            `\n🎉 ALL TESTS PASSED! Challenge Completed!`,
            `----------------------------------------------`,
            `🔑 DEPLOYMENT FLAG SECURED:`,
            `FLAG{T3CH_C0RP_FS_EXP3RT_7721}`,
            `----------------------------------------------`
          ]);
        } else {
          setTestsPassed(false);
          setLogs(prev => [
            ...prev,
            `\n❌ TESTING FAILED. Please resolve errors in red above.`
          ]);
        }
      } else {
        const cacheFixed = isCacheFixed();

        setLogs(prev => [
          ...prev,
          `\n● Running debounce.test.js...`,
          `  ✓ debounce.test.js - Input events successfully debounced (15ms)`,
          `\n● Running caching.test.js...`,
          cacheFixed ? `  ✓ caching.test.js - Duplicate API queries served from custom cache (12ms)` : `  ✕ caching.test.js - Duplicate queries triggered redundant HTTP fetch requests (failed)`
        ]);

        setIsRunningTests(false);
        if (cacheFixed) {
          setTestsPassed(true);
          setLogs(prev => [
            ...prev,
            `\n🎉 ALL TESTS PASSED! Challenge Completed!`,
            `----------------------------------------------`,
            `🔑 DEPLOYMENT FLAG SECURED:`,
            `FLAG{T3CH_FR0NT3ND_VIRTUAL_9918}`,
            `----------------------------------------------`
          ]);
        } else {
          setTestsPassed(false);
          setLogs(prev => [
            ...prev,
            `\n❌ TESTING FAILED. Please resolve caching errors above.`
          ]);
        }
      }
    }, 2000);
  };

  // Helper Auto-Fix helper for quick demonstration/satisfaction
  const applyAutoFix = () => {
    if (isFullStack) {
      setFiles(prev => ({
        ...prev,
        'backend/auth.js': prev['backend/auth.js']
          .replace('const token = authHeader;', 'const token = authHeader && authHeader.split(\' \')[1];'),
        'backend/server.js': prev['backend/server.js']
          .replace('// io.emit(\'task_created\', task);', 'io.emit(\'task_created\', task);')
      }));
      // Auto reload active tab
      setTimeout(() => {
        if (activeFile === 'backend/auth.js') {
          setEditorContent(files['backend/auth.js'].replace('const token = authHeader;', 'const token = authHeader && authHeader.split(\' \')[1];'));
        } else if (activeFile === 'backend/server.js') {
          setEditorContent(files['backend/server.js'].replace('// io.emit(\'task_created\', task);', 'io.emit(\'task_created\', task);'));
        }
      }, 50);
    } else {
      setFiles(prev => ({
        ...prev,
        'src/hooks/useCachedFetch.js': prev['src/hooks/useCachedFetch.js']
          .replace('// TODO: Implement cache lookup (using the cache object) to return memoized data before fetching.', 'if (cache[url]) {\n      setData(cache[url]);\n      return;\n    }')
      }));
      setTimeout(() => {
        if (activeFile === 'src/hooks/useCachedFetch.js') {
          setEditorContent(files['src/hooks/useCachedFetch.js'].replace('// TODO: Implement cache lookup (using the cache object) to return memoized data before fetching.', 'if (cache[url]) {\n      setData(cache[url]);\n      return;\n    }'));
        }
      }, 50);
    }
    setLogs(prev => [
      ...prev,
      `[AI ASSISTANT] Applied codebase optimization and fixes. Re-run tests now!`
    ]);
  };

  // Full Stack Challenge: Add task in browser simulator
  const handleAddTask = (e) => {
    e.preventDefault();
    if (!taskInput.trim()) return;

    if (!authStatus) {
      setNotification({ type: 'error', text: 'Error: Request rejected. JWT validation failed (Unauthorized).' });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    const newTask = {
      id: mockTasks.length + 1,
      title: taskInput,
      completed: false
    };

    if (wsStatus) {
      // Mock socket notification
      setMockTasks(prev => [...prev, newTask]);
      setTaskInput('');
      setNotification({ type: 'success', text: `⚡ WebSocket broadcast: Client received task "${newTask.title}"!` });
      setTimeout(() => setNotification(null), 4000);
    } else {
      // Added in database but other browser tabs won't receive it because of no socket broadcast
      setMockTasks(prev => [...prev, newTask]);
      setTaskInput('');
      setNotification({ type: 'warn', text: `⚠️ Task saved, but WebSocket failed to broadcast real-time sync event.` });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Frontend Challenge: Type in cache search simulator
  const handleQueryChange = (val) => {
    setSearchQuery(val);
    if (!val.trim()) return;

    if (cacheStatus) {
      // Caching is fixed!
      // If we search something we searched recently, we hit the cache
      const isRepeated = ['john', 'admin', 'alice'].includes(val.toLowerCase());
      if (isRepeated) {
        setCacheHitCount(prev => prev + 1);
        setNotification({ type: 'success', text: `✨ Cache HIT! Served "${val}" queries instantly without server request.` });
      } else {
        setNetworkFetchCount(prev => prev + 1);
      }
    } else {
      // Redundant network fetch
      setNetworkFetchCount(prev => prev + 1);
    }
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "#090b11",
      color: "#e2e8f0",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* HEADER SECTION */}
      <div style={{
        height: "56px",
        background: "#0f1320",
        borderBottom: "1px solid #1f293d",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        flexShrink: 0
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            background: "#2563eb",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "700",
            letterSpacing: "1px"
          }}>
            PLATFORM.IO
          </div>
          <span style={{ fontSize: "14px", fontWeight: "600" }}>
            TechCorp Assessment: {isFullStack ? "Full Stack Coding Playground" : "Senior Frontend Optimization Sandbox"}
          </span>
          <span style={{
            background: "rgba(16, 185, 129, 0.15)",
            color: "#10b981",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "600"
          }}>
            ● Sandbox Running
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button 
            onClick={applyAutoFix}
            style={{
              background: "rgba(168, 85, 247, 0.15)",
              color: "#c084fc",
              border: "1px solid rgba(168, 85, 247, 0.3)",
              padding: "6px 14px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "0.2s"
            }}
            title="Auto-implement the solution pattern"
          >
            🪄 Smart Auto-Fix
          </button>
          
          <button 
            onClick={runTestSuite}
            disabled={isRunningTests}
            style={{
              background: isRunningTests ? "#1d4ed8" : "#2563eb",
              color: "#fff",
              border: "none",
              padding: "6px 16px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            {isRunningTests ? "⌛ Testing..." : "▶ Run Tests"}
          </button>

          {testsPassed && (
            <button 
              onClick={() => onSubmitFlag(isFullStack ? 'FLAG{T3CH_C0RP_FS_EXP3RT_7721}' : 'FLAG{T3CH_FR0NT3ND_VIRTUAL_9918}')}
              style={{
                background: "#10b981",
                color: "#fff",
                border: "none",
                padding: "6px 16px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "700",
                cursor: "pointer"
              }}
            >
              🎉 Autofill & Submit Flag
            </button>
          )}

          <button 
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#94a3b8",
              fontSize: "20px",
              cursor: "pointer",
              padding: "4px 8px"
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* CORE WORKSPACE */}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* LEFT COLUMN: FILE TREE (IDE SIDEBAR) */}
        <div style={{
          width: "220px",
          background: "#0c0e17",
          borderRight: "1px solid #1f293d",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0
        }}>
          <div style={{
            padding: "12px 16px",
            fontSize: "11px",
            fontWeight: "700",
            color: "#64748b",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            borderBottom: "1px solid #171d2d"
          }}>
            Workspace Files
          </div>
          
          <div style={{ padding: "8px", display: "flex", flexDirection: "column", gap: "2px" }}>
            {Object.keys(files).map((fileName) => {
              const isActive = activeFile === fileName;
              const isJS = fileName.endsWith('.js');
              const isJSX = fileName.endsWith('.jsx');
              
              return (
                <button
                  key={fileName}
                  onClick={() => selectFile(fileName)}
                  style={{
                    background: isActive ? "rgba(37, 99, 235, 0.12)" : "transparent",
                    color: isActive ? "#3b82f6" : "#94a3b8",
                    border: "none",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    textAlign: "left",
                    fontSize: "13px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.15s"
                  }}
                >
                  <span style={{ color: isJS ? "#f59e0b" : isJSX ? "#38bdf8" : "#94a3b8" }}>
                    {isJS ? '🟨' : isJSX ? '⚛️' : '📄'}
                  </span>
                  <span style={{ fontFamily: "monospace" }}>{fileName}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* MIDDLE COLUMN: EDITOR (IDE CODE AREA) */}
        <div style={{
          flex: 1.2,
          display: "flex",
          flexDirection: "column",
          background: "#090b11",
          borderRight: "1px solid #1f293d"
        }}>
          {/* Editor Header Tab Bar */}
          <div style={{
            height: "36px",
            background: "#0c0e17",
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #1f293d"
          }}>
            <div style={{
              background: "#090b11",
              color: "#3b82f6",
              borderTop: "2px solid #3b82f6",
              padding: "0 18px",
              height: "100%",
              display: "flex",
              alignItems: "center",
              fontSize: "12px",
              fontFamily: "monospace",
              fontWeight: "600"
            }}>
              {activeFile}
            </div>
          </div>

          {/* Textarea code container */}
          <div style={{ flex: 1, position: "relative", display: "flex" }}>
            {/* Mock line numbers gutter */}
            <div style={{
              width: "42px",
              background: "#0c0e17",
              color: "#475569",
              padding: "16px 0",
              textAlign: "right",
              fontSize: "12px",
              fontFamily: "monospace",
              userSelect: "none",
              lineHeight: "1.6",
              boxSizing: "border-box",
              paddingRight: "8px"
            }}>
              {Array.from({ length: 30 }).map((_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>

            <textarea
              value={editorContent}
              onChange={handleEditorChange}
              spellCheck="false"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                color: "#e2e8f0",
                fontSize: "13px",
                fontFamily: "'Space Mono', monospace, Courier New",
                padding: "16px",
                resize: "none",
                outline: "none",
                lineHeight: "1.6",
                boxSizing: "border-box"
              }}
            />
          </div>
        </div>

        {/* RIGHT COLUMN: INSTRUCTIONS & LIVE PREVIEW CONTAINER */}
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: "#0a0c14"
        }}>
          {/* Right Panels Menu */}
          <div style={{
            height: "36px",
            background: "#0c0e17",
            borderBottom: "1px solid #1f293d",
            display: "flex"
          }}>
            <button
              onClick={() => setActiveRightTab('preview')}
              style={{
                flex: 1,
                background: activeRightTab === 'preview' ? "transparent" : "#0c0e17",
                border: "none",
                color: activeRightTab === 'preview' ? "#3b82f6" : "#64748b",
                borderBottom: activeRightTab === 'preview' ? "2px solid #3b82f6" : "none",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer"
              }}
            >
              🌐 App Live Browser Preview
            </button>
            <button
              onClick={() => setActiveRightTab('tests')}
              style={{
                flex: 1,
                background: activeRightTab === 'tests' ? "transparent" : "#0c0e17",
                border: "none",
                color: activeRightTab === 'tests' ? "#3b82f6" : "#64748b",
                borderBottom: activeRightTab === 'tests' ? "2px solid #3b82f6" : "none",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer"
              }}
            >
              🤖 Terminal / Testing Engine
            </button>
          </div>

          {/* RIGHT TABS CONTENT CONTAINER */}
          <div style={{ flex: 1.2, display: "flex", flexDirection: "column", minHeight: 0 }}>
            {activeRightTab === 'preview' ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "16px", gap: "16px", overflowY: "auto" }}>
                {/* Simulated Browser Bar */}
                <div style={{
                  background: "#171d2d",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  border: "1px solid #1f293d"
                }}>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ef4444", display: "inline-block" }}></span>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#f59e0b", display: "inline-block" }}></span>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#10b981", display: "inline-block" }}></span>
                  </div>
                  <div style={{
                    flex: 1,
                    background: "#090b11",
                    borderRadius: "4px",
                    padding: "2px 10px",
                    fontSize: "11px",
                    color: "#94a3b8",
                    fontFamily: "monospace"
                  }}>
                    {isFullStack ? "http://localhost:3000" : "http://localhost:3000/performance-sandbox"}
                  </div>
                </div>

                {/* Mock Application Container */}
                <div style={{
                  flex: 1,
                  background: "#0e111a",
                  borderRadius: "10px",
                  border: "1px solid #1f293d",
                  padding: "20px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px"
                }}>
                  {notification && (
                    <div style={{
                      position: "absolute",
                      top: "10px",
                      left: "10px",
                      right: "10px",
                      background: notification.type === 'error' ? "#ef4444" : notification.type === 'success' ? "#10b981" : "#f59e0b",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: "600",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      zIndex: 10,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                    }}>
                      {notification.text}
                    </div>
                  )}

                  {isFullStack ? (
                    // FULLSTACK BROWSER APP
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #1f293d", paddingBottom: "10px" }}>
                        <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "700" }}>TechCorp Team Tasks</h4>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <span style={{
                            fontSize: "10px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            background: authStatus ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                            color: authStatus ? "#10b981" : "#ef4444"
                          }}>
                            {authStatus ? "JWT Secured" : "JWT: Broken"}
                          </span>
                          <span style={{
                            fontSize: "10px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            background: wsStatus ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                            color: wsStatus ? "#10b981" : "#ef4444"
                          }}>
                            {wsStatus ? "Sockets: Active" : "Sockets: Offline"}
                          </span>
                        </div>
                      </div>

                      <form onSubmit={handleAddTask} style={{ display: "flex", gap: "8px" }}>
                        <input
                          type="text"
                          value={taskInput}
                          onChange={e => setTaskInput(e.target.value)}
                          placeholder="Type a new task title..."
                          style={{
                            flex: 1,
                            background: "#090b11",
                            border: "1px solid #1f293d",
                            borderRadius: "6px",
                            padding: "8px",
                            color: "#fff",
                            fontSize: "12px",
                            outline: "none"
                          }}
                        />
                        <button type="submit" style={{
                          background: "#2563eb",
                          color: "#fff",
                          border: "none",
                          padding: "0 14px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer"
                        }}>
                          Add Task
                        </button>
                      </form>

                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "6px" }}>
                        {mockTasks.map(t => (
                          <div key={t.id} style={{
                            background: "rgba(255,255,255,0.02)",
                            border: "1px solid #1e293b",
                            padding: "10px 12px",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                          }}>
                            <span style={{ fontSize: "12px", textDecoration: t.completed ? "line-through" : "none", color: t.completed ? "#64748b" : "#e2e8f0" }}>
                              {t.title}
                            </span>
                            <span style={{
                              fontSize: "10px",
                              background: t.completed ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)",
                              color: t.completed ? "#10b981" : "#f59e0b",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontWeight: "600"
                            }}>
                              {t.completed ? "Done" : "Pending"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    // FRONTEND BROWSER APP
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #1f293d", paddingBottom: "10px" }}>
                        <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "700" }}>Frontend Rendering Performance</h4>
                        <span style={{
                          fontSize: "10px",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontWeight: "700",
                          background: cacheStatus ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                          color: cacheStatus ? "#10b981" : "#ef4444"
                        }}>
                          {cacheStatus ? "Cache Hook: Optimized" : "Sluggish Fetching Cache"}
                        </span>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", background: "rgba(255,255,255,0.02)", padding: "10px", borderRadius: "6px" }}>
                        <div>
                          <div style={{ fontSize: "9px", color: "#64748b", textTransform: "uppercase" }}>Redundant Network Fetch</div>
                          <div style={{ fontSize: "20px", fontWeight: "700", color: cacheStatus ? "#94a3b8" : "#ef4444", fontFamily: "monospace" }}>
                            {networkFetchCount}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: "9px", color: "#64748b", textTransform: "uppercase" }}>Cache Hits (Instant)</div>
                          <div style={{ fontSize: "20px", fontWeight: "700", color: "#10b981", fontFamily: "monospace" }}>
                            {cacheHitCount}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label style={{ fontSize: "11px", color: "#94a3b8" }}>Search Filter (Try typing: "john", "admin", then type "john" again to test cache hit):</label>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={e => handleQueryChange(e.target.value)}
                          placeholder="Type search terms..."
                          style={{
                            background: "#090b11",
                            border: "1px solid #1f293d",
                            borderRadius: "6px",
                            padding: "8px",
                            color: "#fff",
                            fontSize: "12px",
                            outline: "none"
                          }}
                        />
                      </div>

                      <div style={{ fontSize: "11px", color: "#64748b", lineHeight: "1.4" }}>
                        {cacheStatus 
                          ? "✅ Memoized data successfully loaded. Repeat searches fetch 0 times, preventing redundant re-renders!"
                          : "⚠️ Sluggish behavior detected: Every character typed executes a distinct backend network call, flooding React with useless re-renders."}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              // TESTS & TERMINAL engine
              <div style={{
                flex: 1,
                background: "#07080d",
                color: "#38bdf8",
                padding: "16px",
                fontFamily: "monospace",
                fontSize: "12px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                lineHeight: "1.6"
              }}>
                <div style={{ flex: 1, whiteSpace: "pre-wrap" }}>
                  {logs.map((log, idx) => {
                    const isSuccess = log.includes('✓') || log.includes('ALL TESTS PASSED');
                    const isFail = log.includes('✕') || log.includes('FAILED');
                    const isSystem = log.startsWith('$') || log.startsWith('🔑');
                    
                    let logColor = "#38bdf8";
                    if (isSuccess) logColor = "#10b981";
                    else if (isFail) logColor = "#ef4444";
                    else if (isSystem) logColor = "#a855f7";

                    return (
                      <div key={idx} style={{ color: logColor }}>
                        {log}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* CHALLENGE BRIEF / DETAILED INSTRUCTIONS */}
          <div style={{
            flex: 0.8,
            borderTop: "1px solid #1f293d",
            padding: "16px",
            background: "#0c0e17",
            overflowY: "auto"
          }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>
              📋 Technical Requirements & Guide
            </h4>
            
            {isFullStack ? (
              <div style={{ fontSize: "11.5px", color: "#94a3b8", display: "flex", flexDirection: "column", gap: "8px", lineHeight: "1.5" }}>
                <div>
                  TechCorp's real-time dashboard is experiencing two issues:
                </div>
                <ul style={{ margin: "0", paddingLeft: "16px" }}>
                  <li>
                    <strong>Auth Middleware (`backend/auth.js`):</strong> The middleware reads the raw `Authorization` header directly as the JWT token without stripping out the <code>"Bearer "</code> prefix, failing all secured requests. Extract the token successfully!
                  </li>
                  <li>
                    <strong>Real-time updates (`backend/server.js`):</strong> The WebSocket server is configured, but it doesn't emit the task creation events to connected subscribers. Uncomment the <code>io.emit('task_created', task)</code> socket broadcast statement to sync client dashboards in real time.
                  </li>
                </ul>
                <div style={{ color: "#f59e0b" }}>
                  💡 <em>Hint: Fix code inside the tabs and press the blue <strong>Run Tests</strong> button at the top. You can also click the <strong>🪄 Smart Auto-Fix</strong> button to resolve components instantly!</em>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: "11.5px", color: "#94a3b8", display: "flex", flexDirection: "column", gap: "8px", lineHeight: "1.5" }}>
                <div>
                  The TechCorp search dashboard rendering is slow because duplicate backend network requests execute on every user input change.
                </div>
                <ul style={{ margin: "0", paddingLeft: "16px" }}>
                  <li>
                    <strong>Memoized Cache Hook (`src/hooks/useCachedFetch.js`):</strong> The hook triggers standard `fetch()` queries on every state render. Implement a custom check that inspects the <code>cache[url]</code> object first before initiating network calls. If cache is found, return memoized data instantly!
                  </li>
                </ul>
                <div style={{ color: "#f59e0b" }}>
                  💡 <em>Hint: Fix caching logic under <code>useCachedFetch.js</code> to look up cache hits and press the blue <strong>Run Tests</strong> button. Or click the <strong>🪄 Smart Auto-Fix</strong> wizard!</em>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
